import type {
  HarnessMode,
  HarnessPackSummary,
  StartHarnessRunBody,
} from '@borradh-workspace/api-client/types';
import {
  FolderLock,
  History,
  Search,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';

import { useHarnessPastCases, useStartHarnessRun } from '../../api';

const MAX_SELECTED = 10;

function buildPastRun(
  pack: string,
  mode: HarnessMode,
  caseIds: string[]
): StartHarnessRunBody {
  return { kind: 'past', pack, mode, caseIds };
}

export function PastCasesPanel({
  packs,
  disabled,
  liveUnavailableReason,
  onRun,
  hosted = false,
}: {
  packs: HarnessPackSummary[];
  disabled: boolean;
  liveUnavailableReason: string | null;
  onRun: (runId: string) => void;
  /** Hosted Harness: live replays only (recorded ones need a local stack). */
  hosted?: boolean;
}) {
  const [pack, setPack] = useState<string | null>(packs[0]?.name ?? null);
  const [mode, setMode] = useState<HarnessMode>(hosted ? 'live' : 'recorded');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const { cases, isLoading } = useHarnessPastCases(pack);
  const { startRun, isStarting } = useStartHarnessRun({
    onSuccess: (run) => {
      setSelected(new Set());
      onRun(run.id);
    },
  });

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return cases;
    return cases.filter((item) =>
      [
        item.organization,
        item.id,
        item.customerMessage ?? '',
        item.badReply,
        ...item.issues.map((issue) => issue.label),
      ]
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [cases, search]);

  if (!packs.length) {
    return (
      <Empty className="border border-dashed">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <FolderLock />
          </EmptyMedia>
          <EmptyTitle>No case packs on this machine</EmptyTitle>
          <EmptyDescription>
            Past mistakes come from a private pack of real customer
            conversations that is never committed. Put the pack folder at{' '}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">
              packages/features/src/chatbots/eval/corpus/&lt;pack&gt;/simulation-pack/
            </code>{' '}
            (Daniel has the current ones) and refresh. Keep it on your laptop.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  const ready = (item: (typeof cases)[number]) =>
    mode === 'live' ? item.liveReady && !item.continuation : item.recordedReady;
  const toggle = (id: string, on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (on && next.size < MAX_SELECTED) next.add(id);
      if (!on) next.delete(id);
      return next;
    });
  const run = () => {
    if (!pack) return;
    const body = buildPastRun(pack, mode, [...selected]);
    startRun(body);
  };
  const blockedReason = mode === 'live' ? liveUnavailableReason : null;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Select
          value={pack ?? undefined}
          onValueChange={(value) => {
            setPack(value);
            setSelected(new Set());
          }}
        >
          <SelectTrigger aria-label="Case pack" className="w-full">
            <SelectValue placeholder="Pick a case pack" />
          </SelectTrigger>
          <SelectContent>
            {packs.map((item) => (
              <SelectItem key={item.name} value={item.name}>
                {item.name} · {item.caseCount} cases
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ToggleGroup
          type="single"
          variant="outline"
          value={mode}
          onValueChange={(value) => {
            if (value) {
              setMode(value as HarnessMode);
              setSelected(new Set());
            }
          }}
        >
          <ToggleGroupItem
            value="recorded"
            className="gap-1.5 px-3"
            disabled={hosted}
            title={
              hosted
                ? 'Recorded replays run on a local stack: they need a database they can wipe.'
                : undefined
            }
          >
            <ShieldCheck className="size-4" />
            Recorded
          </ToggleGroupItem>
          <ToggleGroupItem value="live" className="gap-1.5 px-3">
            <Sparkles className="size-4" />
            Live AI
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      <p className="text-muted-foreground text-sm">
        {mode === 'recorded'
          ? 'Replays exactly what Claire said back then through today’s checks. Answers: would that bad reply still go out?'
          : 'Asks today’s Claire the same customer message, with today’s clinic data. Answers: what would she say now?'}
      </p>

      <div className="relative">
        <Search className="absolute top-2.5 left-3 size-4 text-muted-foreground" />
        <Input
          aria-label="Search cases"
          placeholder="Search clinic, message, issue…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <ul className="max-h-[440px] space-y-2 overflow-y-auto pr-1">
          {visible.map((item) => {
            const isReady = ready(item);
            const checked = selected.has(item.id);
            return (
              <li key={item.id}>
                <label
                  htmlFor={`case-${item.id}`}
                  className={cn(
                    'flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors hover:bg-muted/40',
                    checked && 'border-primary/40 bg-primary/5',
                    !isReady && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <Checkbox
                    id={`case-${item.id}`}
                    checked={checked}
                    disabled={
                      !isReady || (!checked && selected.size >= MAX_SELECTED)
                    }
                    onCheckedChange={(value) => toggle(item.id, value === true)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-medium text-sm">
                        {item.organization}
                      </span>
                      <span className="text-muted-foreground">
                        {item.channel.replace('_', ' ')} ·{' '}
                        {new Date(item.replyAt).toLocaleDateString()}
                      </span>
                      {item.issues.slice(0, 3).map((issue) => (
                        <Badge
                          key={issue.label}
                          variant="outline"
                          className={cn(
                            issue.severity === 'high' &&
                              'border-rose-500/30 text-rose-700 dark:text-rose-300'
                          )}
                        >
                          {issue.label}
                        </Badge>
                      ))}
                      {item.continuation ? (
                        <span
                          className="text-muted-foreground"
                          title="Claire sent this as the second message of one answer, so there is no new customer message for Live mode to answer."
                        >
                          second message of a reply
                        </span>
                      ) : null}
                      {!isReady && !(mode === 'live' && item.continuation) ? (
                        <span className="text-muted-foreground">
                          no {mode === 'live' ? 'current' : 'recorded'} data
                        </span>
                      ) : null}
                    </div>
                    {item.customerMessage ? (
                      <p className="line-clamp-1 text-sm">
                        “{item.customerMessage}”
                      </p>
                    ) : null}
                    <p className="line-clamp-2 text-muted-foreground text-sm">
                      Claire: {item.badReply}
                    </p>
                  </div>
                </label>
              </li>
            );
          })}
          {visible.length === 0 ? (
            <li className="py-8 text-center text-muted-foreground text-sm">
              No cases match.
            </li>
          ) : null}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3 border-t pt-4">
        <p className="text-muted-foreground text-sm">
          {selected.size} selected
          {selected.size >= MAX_SELECTED ? ` (max ${MAX_SELECTED})` : ''}
        </p>
        <Button
          onClick={run}
          disabled={
            disabled || Boolean(blockedReason) || isStarting || !selected.size
          }
          title={blockedReason ?? undefined}
        >
          <History className="size-4" />
          Replay {selected.size || ''} case{selected.size === 1 ? '' : 's'}
        </Button>
      </div>
    </div>
  );
}
